import { send, ActorRef, assign, DoneInvokeEvent, forwardTo, createMachine, spawn } from "xstate";
import { Node } from "@iventis/domain-model/model/node";
import { ScheduleVersion } from "@iventis/domain-model/model/scheduleVersion";
import { PermissionOwnerType } from "@iventis/domain-model/model/permissionOwnerType";
import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { PermissionResourceType } from "@iventis/domain-model/model/permissionResourceType";
import { ScheduleStatus } from "@iventis/domain-model/model/scheduleStatus";
import { TwoWayObject, isLocked, isResourceLocked, EMPTY_GUID } from "@iventis/utilities";
import { scheduleApi } from "@iventis/api/src/api";
import { PermissionContext, PermissionEvents, permissionsEditMachine } from "@iventis/permissions/src/permissions-edit.machine";
import { translate } from "@iventis/translations/translation";
import { Content } from "@iventis/translations";
import { deleteDialogMachineConfig, DeleteDialogTypeState } from "./delete-dialog-machine";

export const PERM_MACHINE_ID = "permMachine";

export type EditScheduleContext = {
    originalSchedule: ScheduleVersion;
    latestSchedule: ScheduleVersion;
    permissionsRef: ActorRef<PermissionEvents>;
    permissions: PermissionContext;
    isTopLevelSchedule: boolean;
    node: Node;
};

export type EditScheduleTypeState = (
    | { value: "loading"; context: EditScheduleContext & { originalSchedule: null; latestSchedule: null } }
    | {
          value: "idle";
      }
    | {
          value: { idle: "validation" };
      }
    | {
          value: "idle.validation.valid";
      }
    | {
          value: "idle.validation.invalid";
      }
    | {
          value: "idle.deleteConfirmation";
      }
    | DeleteDialogTypeState<EditScheduleContext, "idle.deleteConfirmation"> // String type concatenation
    | {
          value: "saving";
      }
    | {
          value: "error";
      }
    | {
          value: "finished";
      }
) & { context: EditScheduleContext };

type UpdateNameEvent = {
    type: "UPDATE_NAME";
    payload: string;
};

type UpdateStatusEvent = {
    type: "UPDATE_STATUS";
    payload: ScheduleStatus;
};

type UpdatePermissionsEvent = {
    type: "UPDATE_PERMISSIONS";
    payload: PermissionContext;
};

type RejectEvent = {
    type: "REJECT";
};

type ResolveEvent = {
    type: "RESOLVE";
};

type FetchEvent = {
    type: "FETCH";
};

type SaveEvent = {
    type: "SAVE";
};

type CloseEvent = {
    type: "CLOSE";
};

type DeleteEvent = {
    type: "DELETE";
};

type ConfirmDeleteEvent = {
    type: "CONFIRM_DELETE";
};

type CancelDeleteEvent = {
    type: "CANCEL_DELETE";
};

type RetryEvent = {
    type: "RETRY";
};

export type EditScheduleEvents =
    | SaveEvent
    | CloseEvent
    | DeleteEvent
    | ConfirmDeleteEvent
    | CancelDeleteEvent
    | RetryEvent
    | FetchEvent
    | ResolveEvent
    | RejectEvent
    | UpdateNameEvent
    | UpdatePermissionsEvent
    | UpdateStatusEvent;

const updateStatus = assign({ latestSchedule: (context: EditScheduleContext, event: UpdateStatusEvent) => ({ ...context.latestSchedule, status: event.payload }) });

export const isScheduleEqual = (sch1: ScheduleVersion, sch2: ScheduleVersion) => {
    if (sch1 !== null && sch2 !== null) {
        if (sch1.name !== sch2.name) {
            return false;
        }
        if (sch1.status !== sch2.status) {
            return false;
        }
        return true;
    }
    return true;
};

const isNameValid = (name: string) => typeof name === "string" && name.length !== 0;

const isValid = (context: EditScheduleContext, event: EditScheduleEvents) => {
    switch (event.type) {
        case "UPDATE_NAME": {
            return isNameValid(event.payload);
        }
        default: {
            return isNameValid(context.latestSchedule.name);
        }
    }
};

const isPublishedAndUnlocked = (context: EditScheduleContext, event: UpdateStatusEvent) =>
    event.payload === ScheduleStatus.Published && !isLocked(context.permissions.latestPermissions) && context.permissions.canLock;
const isDraftAndLocked = (context: EditScheduleContext, event: UpdateStatusEvent) =>
    event.payload === ScheduleStatus.Draft && isResourceLocked(context.permissions.latestPermissions, context.node.id) && context.permissions.canLock;
export const EditScheduleMachine = createMachine<EditScheduleContext, EditScheduleEvents, EditScheduleTypeState>(
    {
        id: "editSchedule",
        initial: "loading",
        context: {
            originalSchedule: null,
            latestSchedule: null,
            permissionsRef: null,
            permissions: {
                originalPermissions: null,
                latestPermissions: null,
                localToRemoteMapping: new TwoWayObject<string, string>({}, {}),
                permissionSelector: null,
                errorMessage: null,
                close: false,
                isChild: true,
                canLock: false,
                node: null,
                isNodeCreated: true,
            },
            isTopLevelSchedule: false,
            node: null,
        },
        states: {
            loading: {
                entry: assign({
                    permissionsRef: ({ node, permissions }) => spawn(permissionsEditMachine.withContext({ ...permissions, node }), PERM_MACHINE_ID),
                }),
                invoke: {
                    src: "loadSchedule",
                    onDone: {
                        target: "idle",
                        actions: [
                            assign((context, event: DoneInvokeEvent<ScheduleVersion>) => ({ ...context, originalSchedule: event.data, latestSchedule: event.data })),
                            assign({
                                isTopLevelSchedule: (context) => context.originalSchedule.type.allowedParentType === EMPTY_GUID,
                            }),
                        ],
                    },
                    onError: "error",
                },
            },
            idle: {
                type: "parallel",
                on: {
                    UPDATE_STATUS: [
                        { cond: isPublishedAndUnlocked, target: "#valid", actions: [updateStatus, "lock"] },
                        { cond: isDraftAndLocked, target: "#valid", actions: [updateStatus, "unlock"] },
                        { cond: isValid, target: "#valid", actions: [updateStatus] },
                        { target: "#invalid", actions: updateStatus },
                    ],
                    UPDATE_NAME: [
                        {
                            cond: isValid,
                            target: "#valid",
                            actions: assign({
                                latestSchedule: (context: EditScheduleContext, event: UpdateNameEvent) => ({
                                    ...context.latestSchedule,
                                    name: event.payload,
                                }),
                            }),
                        },
                        {
                            target: "#invalid",
                            actions: assign({
                                latestSchedule: (context: EditScheduleContext, event: UpdateNameEvent) => ({
                                    ...context.latestSchedule,
                                    name: event.payload,
                                }),
                            }),
                        },
                    ],
                    UPDATE_PERMISSIONS: [
                        { cond: isValid, target: "#valid", actions: "updatePermissions" },
                        { target: "#invalid", actions: "updatePermissions" },
                    ],
                    CLOSE: "finished",
                },
                states: {
                    validation: {
                        initial: "valid",
                        states: {
                            valid: {
                                id: "valid",
                                on: {
                                    // Can only save if the form vales are valid
                                    SAVE: {
                                        target: "#saving",
                                        actions: forwardTo(PERM_MACHINE_ID),
                                    },
                                },
                            },
                            invalid: {
                                id: "invalid",
                            },
                        },
                    },
                    deleteConfirmation: deleteDialogMachineConfig<EditScheduleContext, void>("deleteSchedule", "onDelete", "#finished", "#error"),
                },
            },
            saving: {
                id: "saving",
                invoke: {
                    src: "saveSchedule",
                    onDone: {
                        target: "finished",
                        actions: [assign({ originalSchedule: (context) => context.latestSchedule }), "onSave"],
                    },
                    onError: "error",
                },
            },
            finished: {
                id: "finished",
                type: "final",
            },
            error: {
                id: "error",
                type: "final",
            },
        },
    },
    {
        services: {
            saveSchedule: async (context) => {
                scheduleApi.patch<ScheduleVersion>(`/schedule-versions/${context.node.id}`, {
                    name: context.latestSchedule.name,
                    status: context.latestSchedule.status,
                    customErrorMessage: {
                        title: translate(Content.errors.custom_network_error.generic.title, { action: translate(Content.schedule.save_schedule) }),
                        message: translate(Content.errors.custom_network_error.generic.message, { action: translate(Content.schedule.save_schedule) }),
                    },
                });
            },
            deleteSchedule: async (context) => {
                scheduleApi.delete<ScheduleVersion>(`/schedule-versions/${context.node.id}`, {
                    customErrorMessage: {
                        title: translate(Content.errors.custom_network_error.generic.title, { action: translate(Content.schedule.delete_schedule) }),
                        message: translate(Content.errors.custom_network_error.generic.message, { action: translate(Content.schedule.delete_schedule) }),
                    },
                });
            },
            loadSchedule: async (context) => {
                const data = await scheduleApi.get<ScheduleVersion>(`/schedule-versions/${context.node.id}`);
                return data.data;
            },
        },
        actions: {
            updatePermissions: assign<EditScheduleContext, EditScheduleEvents>({
                permissions: (context, event) => {
                    if (event.type === "UPDATE_PERMISSIONS") {
                        return event.payload;
                    }
                    return context.permissions;
                },
            }),
            lock: send(
                (context) => ({
                    type: "ADD_PERMISSION",
                    payload: {
                        id: undefined,
                        ownerType: PermissionOwnerType.Resource,
                        ownerId: context.node.id,
                        ownerName: "Resource Lock",
                        resourceType: PermissionResourceType.ScheduleVersion,
                        resourceId: context.node.id,
                        authorisation: Authorisation.Lock,
                    },
                }),
                { to: PERM_MACHINE_ID }
            ),
            unlock: send(
                (context) => ({
                    type: "REMOVE_PERMISSION",
                    payload: context.permissions.latestPermissions.find((p) => p.authorisation === Authorisation.Lock && p.resourceId === context.node.id),
                }),
                { to: PERM_MACHINE_ID }
            ),
        },
    }
);
